/* @refresh skip */
import { useState } from "react";
import { Mic, MicOff, Volume2, VolumeX, Loader2, X, Sparkles, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useVoiceInterface } from "@/hooks/useVoiceInterface";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

export function VoiceInterface() {
  const { user, session, loading } = useAuth();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const {
    isListening,
    isProcessing,
    isSpeaking,
    transcript,
    responseText,
    startListening,
    stopListening,
    stopSpeaking,
    hasPendingAudio,
    playPendingAudio,
    cancel,
  } = useVoiceInterface();

  // Don't show anything while auth is loading or if no user
  if (loading || !user) return null;

  // Only consider session expired if auth finished loading AND user exists but session is missing
  // This prevents false positives during initial auth state resolution
  const isSessionExpired = !loading && user && !session;

  const handleMicClick = () => {
    // If session expired, redirect to login
    if (isSessionExpired) {
      navigate("/auth?returnUrl=/dashboard");
      return;
    }

    // iOS autoplay policy: if we have pending audio, play it on this user gesture
    if (hasPendingAudio && !isListening && !isProcessing) {
      playPendingAudio();
      return;
    }
    
    if (isListening) {
      stopListening();
    } else {
      setIsExpanded(true);
      startListening();
    }
  };

  const handleClose = () => {
    cancel();
    setIsExpanded(false);
  };

  const isActive = isListening || isProcessing || isSpeaking;
  const showButton = !isExpanded;

  return (
    <>
      {/* Floating Voice Button */}
      <AnimatePresence>
        {showButton && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-6 right-24 z-50"
          >
            <Button
              onClick={handleMicClick}
              size="lg"
              variant="outline"
              className={cn(
                "h-14 w-14 rounded-full shadow-lg border-2",
                "bg-background hover:bg-primary/5",
                "border-primary/30 hover:border-primary"
              )}
            >
              <Mic className="h-6 w-6 text-primary" />
              <span className="sr-only">Falar com Sentinela</span>
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded Voice Interface */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-50 w-[90vw] max-w-sm"
          >
            <Card className="shadow-2xl border-primary/20 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Volume2 className="h-5 w-5" />
                    <div>
                      <h3 className="text-base font-semibold">Sentinela por Voz</h3>
                      <p className="text-xs text-primary-foreground/70">
                        {isListening
                          ? "Ouvindo..."
                          : isProcessing
                          ? "Processando..."
                          : isSpeaking
                          ? "Falando..."
                          : "Toque para falar"}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-primary-foreground hover:bg-white/20"
                    onClick={handleClose}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <CardContent className="p-4 space-y-4">
                {/* Session Expired State */}
                {isSessionExpired ? (
                  <div className="text-center py-6 space-y-4">
                    <div className="mx-auto h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                      <LogIn className="h-8 w-8 text-destructive" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-base">Sessão expirada</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Por favor, faça login novamente para usar a interface de voz.
                      </p>
                    </div>
                    <Button 
                      onClick={() => navigate("/auth?returnUrl=/dashboard")}
                      className="gap-2"
                    >
                      <LogIn className="h-4 w-4" />
                      Fazer login
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Voice Animation */}
                    <div className="flex justify-center py-4">
                      <motion.button
                        onClick={handleMicClick}
                        disabled={isProcessing}
                        className={cn(
                          "relative h-24 w-24 rounded-full flex items-center justify-center transition-all",
                          isListening
                            ? "bg-red-500 hover:bg-red-600"
                            : isProcessing
                            ? "bg-yellow-500 cursor-wait"
                            : isSpeaking
                            ? "bg-emerald-500"
                            : "bg-primary hover:bg-primary/90"
                        )}
                        whileTap={{ scale: 0.95 }}
                      >
                        {/* Pulse animation when listening */}
                        {isListening && (
                          <>
                            <motion.div
                              className="absolute inset-0 rounded-full bg-red-500"
                              animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                            />
                            <motion.div
                              className="absolute inset-0 rounded-full bg-red-500"
                              animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0, 0.3] }}
                              transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                            />
                          </>
                        )}

                        {/* Speaking animation */}
                        {isSpeaking && (
                          <motion.div
                            className="absolute inset-0 rounded-full bg-emerald-500"
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 0.5, repeat: Infinity }}
                          />
                        )}

                        {/* Icon */}
                        <div className="relative z-10 text-white">
                          {isListening ? (
                            <MicOff className="h-10 w-10" />
                          ) : isProcessing ? (
                            <Loader2 className="h-10 w-10 animate-spin" />
                          ) : isSpeaking ? (
                            <Volume2 className="h-10 w-10" />
                          ) : (
                            <Mic className="h-10 w-10" />
                          )}
                        </div>
                      </motion.button>
                    </div>

                    {/* Status Text */}
                    <div className="text-center space-y-2">
                      {!isActive && !transcript && !responseText && (
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">
                            Toque no microfone e pergunte:
                          </p>
                          <p className="text-sm font-medium text-primary">
                            "Sentinela, como foi meu mês?"
                          </p>
                        </div>
                      )}

                      {isListening && (
                        <div className="flex items-center justify-center gap-2">
                          <motion.div
                            className="flex gap-1"
                            animate={{ opacity: [1, 0.5, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          >
                            <div className="h-2 w-2 rounded-full bg-red-500" />
                            <div className="h-2 w-2 rounded-full bg-red-500" />
                            <div className="h-2 w-2 rounded-full bg-red-500" />
                          </motion.div>
                          <span className="text-sm text-muted-foreground">
                            Ouvindo sua pergunta...
                          </span>
                        </div>
                      )}

                      {transcript && (
                        <div className="bg-muted rounded-lg p-3 text-left">
                          <p className="text-xs text-muted-foreground mb-1">Você disse:</p>
                          <p className="text-sm">{transcript}</p>
                        </div>
                      )}

                      {isProcessing && (
                        <div className="flex items-center justify-center gap-2">
                          <Sparkles className="h-4 w-4 text-yellow-500 animate-pulse" />
                          <span className="text-sm text-muted-foreground">
                            Analisando...
                          </span>
                        </div>
                      )}

                      {responseText && !isSpeaking && !isProcessing && (
                        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-left">
                          <p className="text-xs text-muted-foreground mb-1">Resposta:</p>
                          <p className="text-sm line-clamp-4">{responseText}</p>
                          {hasPendingAudio && (
                            <div className="mt-2 flex items-center justify-between gap-2">
                              <p className="text-xs text-muted-foreground">
                                Toque para ouvir.
                              </p>
                              <Button size="sm" onClick={playPendingAudio} className="gap-2">
                                <Volume2 className="h-4 w-4" />
                                Ouvir resposta
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {isSpeaking && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={stopSpeaking}
                          className="gap-2"
                        >
                          <VolumeX className="h-4 w-4" />
                          Parar
                        </Button>
                      )}
                    </div>

                    {/* Example Questions */}
                    {!isActive && !transcript && (
                      <div className="border-t pt-3">
                        <p className="text-xs text-muted-foreground mb-2">
                          Exemplos de perguntas:
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {[
                            "Como está meu caixa?",
                            "Meu CMV está bom?",
                            "Qual minha situação?",
                          ].map((q) => (
                            <span
                              key={q}
                              className="text-xs bg-muted px-2 py-1 rounded-full"
                            >
                              {q}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
